import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import {
  getScheduledPaymentById,
  linkSpItems,
} from "../../api/scheduledPayments";
import { getReceiptAcknowledgements } from "../../api/reciptAcknowledgement";
import { getRaPaymentsByReceipt } from "../../api/raPayments";
import { getStatusCatalogue } from "../../api/common/catalogues.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { formatCurrency } from "../../services/valueFormatters";

export default function ScheduledPaymentLinkItemsModal({ props }) {
  const { id } = props;
  const { openSnack } = useSnack();

  const [scheduledPayment, setScheduledPayment] = useState(null);
  const [raCreatedStatusId, setRaCreatedStatusId] = useState(null);
  // selections[spItemId] = { receipt, raPayment, installments }
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);

  const loadScheduledPayment = async () => {
    const response = await getScheduledPaymentById(id);
    setScheduledPayment(response.data);
  };

  useEffect(() => {
    loadScheduledPayment();
    getStatusCatalogue("RECEIPT_ACKNOWLEDGEMENT").then((response) => {
      const status = response.data.find((s) => s.code === "RA_CREATED");
      setRaCreatedStatusId(status?.id ?? null);
    });
  }, [id]);

  if (!scheduledPayment) return null;

  const unlinkedItems = scheduledPayment.items.filter((item) => !item.raPaymentId);

  const handleReceiptChange = async (spItemId, receipt) => {
    if (!receipt) {
      setSelections((prev) => ({ ...prev, [spItemId]: undefined }));
      return;
    }
    const response = await getRaPaymentsByReceipt(receipt.id);
    setSelections((prev) => ({
      ...prev,
      [spItemId]: { receipt, raPayment: null, installments: response.data },
    }));
  };

  const handleInstallmentChange = (spItemId, raPaymentId) => {
    setSelections((prev) => {
      const current = prev[spItemId];
      if (!current) return prev;
      const raPayment = current.installments.find((i) => i.id === raPaymentId) || null;
      return { ...prev, [spItemId]: { ...current, raPayment } };
    });
  };

  const handleSubmit = async () => {
    const links = Object.entries(selections)
      .filter(([, selection]) => selection?.raPayment)
      .map(([spItemId, selection]) => ({
        spItemId: Number(spItemId),
        raPaymentId: selection.raPayment.id,
      }));

    if (links.length === 0) {
      openSnack("Selecciona al menos una cuota para vincular", "warning");
      return;
    }

    try {
      setLoading(true);
      await linkSpItems(id, links);
      openSnack("Renglones vinculados exitosamente", "success");
      setSelections({});
      await loadScheduledPayment();
    } catch (error) {
      openSnack(
        error.response?.data?.Details || error.message || "Error al vincular los renglones",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={1} minWidth={500}>
      <Typography variant="body2" mb={2}>
        Vincula cada renglón sin asignar con una cuota de un contrarrecibo creado del proveedor{" "}
        <strong>{scheduledPayment.provider?.code}</strong>.
      </Typography>

      {unlinkedItems.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Todos los renglones de esta corrida ya están vinculados.
        </Typography>
      )}

      <Stack spacing={2} divider={<Divider />}>
        {unlinkedItems.map((item) => {
          const selection = selections[item.id];
          return (
            <Box key={item.id}>
              <Typography variant="subtitle2">
                {item.description || `Renglón #${item.id}`} — {formatCurrency(item.amount)}
              </Typography>
              <Stack direction="row" spacing={2} mt={1}>
                <Box flex={1}>
                  <LGAutocomplete
                    fetchFunction={getReceiptAcknowledgements}
                    fetchBy="code"
                    queryParams={{
                      companyId: scheduledPayment.companyId,
                      providerCode: scheduledPayment.provider?.code,
                      statusId: raCreatedStatusId,
                    }}
                    optionLabel="code"
                    value={selection?.receipt || null}
                    onChange={(event, value) => handleReceiptChange(item.id, value)}
                    label="Contrarrecibo"
                  />
                </Box>
                <FormControl sx={{ minWidth: 260 }} disabled={!selection}>
                  <InputLabel>Cuota</InputLabel>
                  <Select
                    label="Cuota"
                    value={selection?.raPayment?.id || ""}
                    onChange={(e) => handleInstallmentChange(item.id, e.target.value)}
                  >
                    {(selection?.installments || []).map((installment) => (
                      <MenuItem key={installment.id} value={installment.id}>
                        Cuota {installment.installmentNo} — {installment.amount} —{" "}
                        {installment.paymentDate}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {unlinkedItems.length > 0 && (
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Vinculando..." : "Vincular"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
